<template>
  <div class="dashboard-content">
    <Loader v-if="!isAuthenticationInitialized || (isAuthenticated && !isProfileLoaded)" />

    <template v-else>
      <!--SuperUser customer selection-->
      <div class="dashboard-content__superuser-panel">
        <SuperUserActionPanel>
          <SuperUserCustomerPicker v-if="isProfileLoaded" @customerSelected="superUserCustomerSelected" />
        </SuperUserActionPanel>
      </div>

      <!-- Greeting section -->
      <div class="greeting-section dashboard-content__greeting-section">
        <div class="greeting-section__title-n-text">
          <template v-if="!isAuthenticated">
            <div class="global-h1 greeting-section__title">Welcome to Shrpa!</div>
            <div class="greeting-section__text">
              <p>New to Shrpa? Just authenticate with {{ emailHint ? emailHint : "the email your invite was sent to" }}. It's as easy as that!</p>
              <div>
                <button class="ui primary button" @click="isShowLoginModal = true">Continue</button>
              </div>
            </div>
          </template>

          <template v-if="isProfileLoaded">
            <!-- Not an admin -->
            <template v-if="!communityIdInContext && !isSuperOrSalesUser">
              <div class="global-h1 greeting-section__title">Hi There!</div>
              <div class="greeting-section__text">
                <p v-if="checkIfSignedInWithDifferentEmailFromSignUpHint">
                  It looks like {{ emailHint }} was invited but you are currently logged in as {{ getCurrentUsersEmail() }}.<br />
                  Try logging in with {{ emailHint }} or email <CopyTextNew>support@shrpa.com</CopyTextNew> to setup another admin email. Thanks!
                  <br />
                  <br />
                  <button class="ui primary button" @click="logout">Logout</button>
                </p>
                <p v-else>
                  It doesn't look like we have you set up as a community admin.<br />
                  If you think this is in error, please email <CopyTextNew>support@shrpa.com</CopyTextNew>
                </p>
              </div>
            </template>

            <!-- Customer Admin intro-->
            <div v-else>
              <div class="global-h1 greeting-section__title greeting-section__title--with-select">
                {{ greeting }}

                <!--Multiple Destination Admin-->
                <select v-if="communityData.length > 1" class="global-select greeting-section__community-select" v-model="communityIdInContext" @change="communityChanged">
                  <option v-for="(community, index) in communityData" :key="index" :value="community.communityId">
                    {{ community.communityName }}
                  </option>
                </select>
              </div>
              <div class="greeting-section__text">
                <p>Curated content to highlight your destination!&nbsp; Send any questions to <CopyTextNew>support@shrpa.com</CopyTextNew></p>

                <NoteWithIcon v-if="isCurrentCustomerDisabled" color="red" style="margin-top: 15px">
                  <template #icon><IconEmbedded name="warning_2-5" style="position: relative; top: -2px" /></template>
                  <template #text>
                    <div>
                      <h3 class="global-h3" style="margin-bottom: 3px">This account is no longer active.</h3>
                      <p>Reach out to <CopyText :text="'sales@shrpa.com'" /> to learn about the benefits of renewing!</p>
                    </div>
                  </template>
                </NoteWithIcon>

                <NoteWithIcon v-else-if="showSubscriptionEnding || showSubscriptionRenewing" color="yellow" style="margin-top: 15px">
                  <template #icon><IconEmbedded name="info-simple_4" /></template>
                  <template #text>
                    <div>
                      <template v-if="showSubscriptionEnding">
                        <h4 class="global-h4" style="margin-bottom: 3px">Your subscription is ending soon!</h4>
                        <div>At that time, Photo Walls, Adventure Walls, and Adventure Posts will no longer be displayed.</div>
                        <div style="margin-top: 10px; margin-bottom: 10px">Reach out if you have any questions or want to discuss potential renewal options.</div>
                      </template>
                      <template v-else>
                        <h4 class="global-h4" style="margin-bottom: 3px">Your subscription is renewing soon!</h4>
                      </template>
                      <RouterLink :to="{ name: 'SubscriptionSettings', params: { pageId: communityIdInContext } }" class="global-a global-a--orange"> View or Manage your Subscription </RouterLink>
                    </div>
                  </template>
                </NoteWithIcon>
              </div>
            </div>
            <!-- / Customer Admin intro-->
          </template>
        </div>
      </div>
      <!-- / Greeting section -->

      <!-- Authenticated Content-->
      <template v-if="isProfileLoaded && (communityIdInContext || isSuperOrSalesUser)">
        <div class="dashboard-content__horizontal-divider"></div>

        <!-- Atlas and Creator Visits -->
        <div v-if="currentCustomerInContext?.subType === 'Original' || currentCustomerInContext?.subType?.startsWith('Trial')">
          <AtlasDashboardWidget v-if="serverFlagEnableAtlasSocial" style="margin-bottom: 55px" :customerId="communityIdInContext" :isCustomerDisabled="isCurrentCustomerDisabled" />
          <AtlasOptInHero v-else style="margin-bottom: 45px" :isMiniView="true" :customerId="communityIdInContext" />

          <div class="dashboard-content__horizontal-divider"></div>

          <!-- Collab Summaries  section -->
          <Loader v-if="isCollabAndCreatorSummaryArrayLoading" style="margin: -10px 0 30px" />
          <template v-else>
            <MyActiveCollabsSection
              :collabAndCreatorSummaryArray="collabAndCreatorSummaryArray"
              :communityId="communityIdInContext"
              :isAddMoreCollabsButtonVisible="collabsRemaining < 1"
              :isCustomerDisabled="isCurrentCustomerDisabled"
              style="margin-bottom: 45px"
              @clickAddMoreCollabsButton="isAddMoreCreatorVisitsModalVisible = true"
            />

            <AddMoreCreatorVisitsModal v-model:isVisible="isAddMoreCreatorVisitsModalVisible" :communityId="communityIdInContext" @purchase="loadCommunityData" />

            <div class="dashboard-content__horizontal-divider" style="margin-bottom: 45px"></div>
          </template>
        </div>
        <!-- / Social Snapshots and Creator Visits -->

        <Loader v-if="isLoadingCommunityData" />
        <template v-else>
          <div class="spotlight-dashboard-wrapper" style="margin-bottom: 60px">
            <!-- Community Uploads and Custom Collabs -->
            <div v-if="currentCustomerInContext?.subType !== 'CustomCollab'" class="spotlight-dashboard">
              <Loader v-if="isCommunityUploadDataLoading" />

              <template v-else>
                <!-- Main header -->
                <div class="main-header spotlight-dashboard__main-header">
                  <RouterLink class="global-h2 main-header__title" :to="{ name: 'UserUploadCampaigns', params: { pageId: communityIdInContext } }"> Community Uploads </RouterLink>
                  <div class="main-header__subtitle">Tap into your community's creativity — seamlessly collect photos and videos that capture genuine local perspectives.</div>
                </div>
                <!-- / Main header -->

                <template v-if="!latestCampaign">
                  <!-- Upload campaigns -->
                  <div class="upload-campaigns spotlight-dashboard__upload-campaigns">
                    <ul class="upload-campaigns__template-snippets-list">
                      <UploadCampaignTemplateSnippet
                        class="upload-campaigns__template-snippet"
                        v-for="templateTheme in Object.keys(uploadCampaignTemplates)"
                        :key="templateTheme"
                        :template="uploadCampaignTemplates[templateTheme]"
                        :templateTheme="templateTheme as UploadCampaignTemplateTheme"
                        @select="
                          templateTheme => $router.push({ name: 'UserUploadCampaigns', params: { pageId: communityIdInContext }, query: { openCreateCampaignModalWithTemplate: templateTheme } })
                        "
                      />
                    </ul>
                  </div>
                  <!-- / Upload campaigns -->
                </template>

                <template v-else>
                  <div class="upload-campaigns spotlight-dashboard__upload-campaigns" style="margin-bottom: 20px">
                    <ul class="upload-campaigns__upload-campaign-snippets-list">
                      <UploadCampaignSnippet
                        v-if="latestCampaign"
                        class="upload-campaigns__upload-campaign-snippet"
                        :uploadCampaign="latestCampaign"
                        :pageId="communityIdInContext"
                        :showRemoveButton="false"
                        @clickOnTitle="
                          $router.push({
                            name: 'UserUploadCampaigns',
                            params: { pageId: communityIdInContext },
                            query: { campaignToEditId: latestCampaign.campaign.campaignId },
                          })
                        "
                        @archive="
                          $router.push({
                            name: 'UserUploadCampaigns',
                            params: { pageId: communityIdInContext },
                            query: { campaignToArchiveId: latestCampaign.campaign.campaignId },
                          })
                        "
                        @share="
                          (_, tabNameToOpen) => {
                            campaignToShare = latestCampaign.campaign;
                            isSpreadTheWordModalVisible = true;
                            if (tabNameToOpen) {
                              spreadTheWordModalTabToOpen = tabNameToOpen;
                            }
                          }
                        "
                      />
                    </ul>
                  </div>

                  <SpreadTheWordModal
                    v-if="campaignToShare"
                    v-model:isVisible="isSpreadTheWordModalVisible"
                    :pageId="communityIdInContext"
                    :campaign="campaignToShare"
                    :customerUniqueName="campaignData.customerIdentifierForUri"
                    :tabToOpen="spreadTheWordModalTabToOpen"
                  />

                  <SrpButton
                    tag="RouterLink"
                    :to="{
                      name: 'UserUploadCampaigns',
                      params: { pageId: communityIdInContext },
                      query: { openCreateCampaignModal: 'true' },
                    }"
                    :isDisabled="isCurrentCustomerDisabled"
                    size="small"
                    fill="outlined"
                    style="margin: 0 15px 25px 0"
                  >
                    <template #icon>
                      <IconEmbedded name="plus_4" :size="18" />
                    </template>
                    {{ campaignData?.campaigns?.length ? "Create another Upload Link" : "Create Upload Link" }}
                  </SrpButton>

                  <div style="width: 100%"></div>

                  <LinkWithIcon
                    v-if="campaignData?.campaigns?.length > 1"
                    style="margin-bottom: 30px"
                    iconInset="auto -5px auto auto"
                    color="blue"
                    tag="RouterLink"
                    :to="{ name: 'UserUploadCampaigns', params: { pageId: communityIdInContext } }"
                  >
                    <span>All upload campaigns</span>
                    <template #iconRight><IconEmbedded name="arrow-right_2" :size="20" /></template>
                  </LinkWithIcon>
                </template>

                <div class="spotlight-dashboard__divider"></div>

                <!-- Photo walls section -->
                <div class="photo-walls-section spotlight-dashboard__photo-walls">
                  <div class="photo-walls-section__header">
                    <RouterLink class="global-h2 photo-walls-section__title" :to="{ name: 'WidgetDirections', params: { pageId: communityIdInContext } }"> Photo Walls </RouterLink>
                    <div class="photo-walls-section__subtitle">Elevate your website with dynamic, easy-to-build photo walls featuring your community's most engaging content.</div>
                  </div>

                  <img class="photo-walls-section__example-img" src="https://cdn.shrpa.com/images/misc/integrations/photowall-example.jpg" alt="example" />

                  <SrpButton
                    class="create-photowall-snippet__cta-button"
                    fill="outlined"
                    size="small"
                    tag="RouterLink"
                    :to="{
                      name: 'WidgetDirections',
                      params: { pageId: communityIdInContext },
                    }"
                  >
                    <template #icon>
                      <IconEmbedded name="plus_4" :size="18" />
                    </template>
                    Set up a Photowall
                  </SrpButton>
                </div>
                <!-- / Photo walls section -->
              </template>
            </div>
            <!-- / Community Uploads and Custom Collabs -->

            <template v-if="!isCustomCollabsLoading && customCollabs.length > 0">
              <div class="spotlight-dashboard__divider"></div>

              <MyActiveCustomCollabsSection
                :collabAndCreatorSummaryArray="collabAndCreatorSummaryArray"
                :customCollabs="customCollabs"
                :communityId="communityIdInContext"
                :isCustomerDisabled="isCurrentCustomerDisabled"
                @clickStartANewCustomCollab="isAddMoreCreatorVisitsModalVisible = true"
              />
            </template>
          </div>

          <div class="dashboard-content__horizontal-divider" style="margin-bottom: 45px"></div>

          <!--Activity Feed-->
          <ActivityFeed viewingContext="customer" :allowShowMore="false" style="max-width: 720px" />

          <WeLoveToVisitSnippet class="dashboard-content__we-love-snippet" :destinationName="currentCommunityName" />
        </template>
      </template>
      <!-- / Authenticated Widgets-->

      <!--Modals-->
      <!--NOTE: The v-if is required here because of the logic in mount() in the LoginModal.  Should consider reworking at some point.-->
      <LoginModal v-if="isShowLoginModal" :startWithNewUserFlow="true" :skipEmailInput="true" :isVisible="isShowLoginModal" :defaultEmail="emailHint" @close="isShowLoginModal = false" />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import axios from "axios";
import { getInstance } from "@auth/authWrapper";
import AdminContext from "@logic/AdminContext";
import { RouteHelper } from "@helpers/RouteHelper";
import { useHead } from "@unhead/vue";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { useUserProfileStore } from "@stores/userProfileStore";
import { mapState } from "pinia";
import CustomerCustomCollabsRepo from "@repos/CustomerCustomCollabsRepo";
import uploadCampaignTemplatesRaw from "@views/Community/UserUploadCampaigns/uploadCampaignTemplates.json";
import SpreadTheWordModal from "@views/Community/UserUploadCampaigns/SpreadTheWordModal.vue";
import UploadCampaignSnippet from "@views/Community/UserUploadCampaigns/UploadCampaignSnippet.vue";
import UploadCampaignTemplateSnippet from "@views/Community/UserUploadCampaigns/UploadCampaignTemplateSnippet.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import FeatureFlagCache from "@logic/FeatureFlagCache";

// Types
import { CustomerDashboardSummary } from "@contracts/pages";
import { CustomerCollabActionSummary } from "@contracts/collabActionSummaries";
import { CustomCollabWidgetPayload } from "@contracts/customCollabs";
import { UserContentUploadCampaignData, UserContentUploadCampaignDataV2, UserUploadCampaignsV2 } from "@contracts/userContentUploadCampaignData";
import { UploadCampaignTemplateTheme, UploadCampaignTemplate } from "@contracts/uploadCampaignTemplates";

// Components
import CopyText from "@components/CopyText.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import Loader from "@components/Loader/Loader.vue";
import LoginModal from "@components/Modals/LoginModal.vue";
import WeLoveToVisitSnippet from "@components/WeLoveToVisitSnippet.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import ActivityFeed from "@components/ActivityFeed/index.vue";
import MyActiveCollabsSection from "./MyActiveCollabsSection.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import AddMoreCreatorVisitsModal from "@components/Modals/AddMoreCreatorVisitsModal.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";
import MyActiveCustomCollabsSection from "./MyActiveCustomCollabsSection.vue";
import AtlasOptInHero from "@components/AtlasOptInHero.vue";
import SocialPostPreview from "@components/SocialPostPreview/index.vue";
import SuperUserCustomerPicker from "@components/SuperUserCustomerPicker.vue";
import { SpreadTheWordTabName } from "@views/Community/UserUploadCampaigns/SpreadTheWordModal.vue";
import AtlasDashboardWidget from "@components/AtlasDashboardWidget.vue";

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: "Dashboard",

  components: {
    AtlasOptInHero,
    AddMoreCreatorVisitsModal,
    SrpButton,
    MyActiveCollabsSection,
    ActivityFeed,
    IconEmbedded,
    CopyText,
    CopyTextNew,
    Loader,
    LoginModal,
    WeLoveToVisitSnippet,
    NoteWithIcon,
    SuperUserActionPanel,
    MyActiveCustomCollabsSection,
    SpreadTheWordModal,
    UploadCampaignSnippet,
    UploadCampaignTemplateSnippet,
    LinkWithIcon,
    SocialPostPreview,
    SuperUserCustomerPicker,
    AtlasDashboardWidget,
  },

  data() {
    return {
      isAuthenticationInitialized: false,
      isAuthenticated: false,
      isShowLoginModal: false,
      isProfileLoaded: false,
      communityIdInContext: null as string | null,
      title: "Admin Dashboard",
      // Note: We could probably remove this and just rely on the communityData
      adminOf: [] as Array<string>,
      isMultipleAdminSectionVisible: true,
      communityData: [] as Array<CustomerDashboardSummary>,
      isLoadingCommunityData: true,
      userProfileStore: useUserProfileStore(),
      // SuperUser fields
      superUserManuallyAddedCommunityIds: [] as Array<string>,
      // Improved login flow
      emailHint: null as string,
      isEmailHint: false,
      isEmailHintGmail: false,

      isAddMoreCreatorVisitsModalVisible: false,

      isCollabAndCreatorSummaryArrayLoading: true,
      collabAndCreatorSummaryArray: [] as Array<CustomerCollabActionSummary>,

      isCustomCollabsLoading: true,
      customCollabs: [] as Array<CustomCollabWidgetPayload>,

      // Community Uploads data
      isCommunityUploadDataLoading: false,
      campaignData: null as UserUploadCampaignsV2 | null,
      campaignToShare: null as UserContentUploadCampaignData | null,
      isSpreadTheWordModalVisible: false,
      spreadTheWordModalTabToOpen: null as SpreadTheWordTabName | null,
      uploadCampaignTemplates: uploadCampaignTemplatesRaw as Record<UploadCampaignTemplateTheme, UploadCampaignTemplate>,

      // Feature flags
      serverFlagEnableAtlasSocial: false,
    };
  },

  computed: {
    ...mapState(useUserProfileStore, ["getViewingUserProfile", "getActingUserProfile", "isSuperOrSalesUser"]),
    collabsRemaining(): number {
      const currentCommunity = this.communityData?.find(cd => cd.communityId === this.globalRoot.orgInContext.customerId);
      return currentCommunity?.remainingCollabCount ?? 0;
    },

    greeting(): string {
      if (this.getViewingUserProfile?.firstName && !this.getViewingUserProfile.firstName.includes("@")) {
        return `Welcome ${this.getViewingUserProfile.firstName}`;
      }
      return "Welcome";
    },
    currentCommunityName(): string {
      if (!this.communityIdInContext || !this.communityData) {
        return "";
      }

      const community = this.communityData.filter(c => c.communityId === this.communityIdInContext);

      if (community && community.length > 0) {
        return community[0].communityName;
      }

      return "";
    },
    currentCustomerInContext() {
      if (!this.communityIdInContext || !this.communityData) return null;
      const customerInContext = this.communityData.find(c => c.communityId === this.communityIdInContext);

      return customerInContext ?? null;
    },
    showSubscriptionRenewing(): boolean {
      return this.currentCustomerInContext?.showSubscriptionRenewing ?? false;
    },
    showSubscriptionEnding(): boolean {
      return this.currentCustomerInContext?.showSubscriptionEnding ?? false;
    },
    isCurrentCustomerDisabled(): boolean {
      return this.currentCustomerInContext?.isDisabled ?? false;
    },
    checkIfSignedInWithDifferentEmailFromSignUpHint(): boolean {
      if (this.emailHint) {
        const currentUserEmail = this.getCurrentUsersEmail();
        if (currentUserEmail) {
          // If they're signed in with an account that's not the hint we show them a special message.
          console.info(`checkIfSignedInWithDifferentEmailFromSignUpHint: currentUserEmail=${currentUserEmail}, invitedUser=${this.emailHint}`);
          return currentUserEmail.toLowerCase() !== this.emailHint.toLowerCase();
        }
      }
      return false;
    },
    latestCampaign(): UserContentUploadCampaignDataV2 | null {
      const activeCampaigns = this.campaignData?.campaigns?.filter(c => !c.campaign.expiryDateTime);
      if (activeCampaigns?.length) {
        return activeCampaigns?.reduce((a, b) => {
          return new Date(b.campaign.createdDate) > new Date(a.campaign.createdDate) ? b : a;
        });
      } else {
        return null;
      }
    },
  },

  watch: {
    // Little hacky but works
    "userProfileStore.loggedInUser": function (profile) {
      getGlobalRemoteLogger().info("Dashboard: loggedInUser watch fired");
      if (profile) {
        this.profileLoaded();
      }
    },
    $route(to, from) {
      // Needed since the left nav uses the redirect parameters currently
      this.checkForRedirect();
    },
  },

  async mounted() {
    useHead({ title: () => this.title ?? "" });

    /* Note: This Init sequence is fairly complicated since we want to handle several scenarios:
     * 1. Auth is still loading (so we don't know much yet)
     * 2. User is not logged in
     * 3. Profile is loaded (user must be logged in)
     *
     * Additional logic pivots off that to handle edge cases like (has a profile but )
     */
    this.checkForEmailHint();
    const authService = getInstance();

    if (authService.loading === false) {
      this.authLoaded();
    }

    authService.$watch("loading", async loading => {
      if (loading === false) {
        this.authLoaded();
      }
    });

    // This code is used if they got here via a link on the site.
    // On a fresh load, the watch will catch them once their profiles loads (after auth)
    // @ts-ignore
    if (this.getActingUserProfile) {
      getGlobalRemoteLogger().info("Dashboard: Profile already loaded");
      await this.profileLoaded();
    }
  },

  methods: {
    authLoaded() {
      this.isAuthenticationInitialized = true;
      const authService = getInstance();
      this.isAuthenticated = authService.isAuthenticated;
      getGlobalRemoteLogger().info(`Dashboard.AuthLoaded: IsAuthenticated=${this.isAuthenticated}`);
    },
    async profileLoaded() {
      getGlobalRemoteLogger().info(`ProfileLoaded`);
      // @ts-ignore
      const adminOf = this.getActingUserProfile.adminOf;

      if (adminOf && adminOf.length > 0) {
        this.adminOf = adminOf;
        this.communityIdInContext = AdminContext.getCommunityIdInContext();
        getGlobalRemoteLogger().info(`Setting context to ${this.communityIdInContext}. Is adminOf ${adminOf.length} community(s)`);

        // Check for redirects to specific admin pages
        this.checkForRedirect();
        await this.checkForSpotlightCheckoutId();
      }
      // Formerly had a redirect to the spotlight onboarding

      this.isProfileLoaded = true;

      await this.checkForSuperUserFields();
      await this.loadData();
    },
    checkForRedirect() {
      // This provides quick redirects w/out having the communityId in the uri,
      // which makes it much easier to template emails
      let redirect = RouteHelper.getQueryStringParam("r");
      if (!redirect) redirect = RouteHelper.getQueryStringParam("r2");
      if (redirect) {
        // Ensure CommunityIdInContext is set
        if (!this.communityIdInContext) {
          if (this.getViewingUserProfile?.adminOf.length > 0) this.communityIdInContext = this.getViewingUserProfile.adminOf[0];
        }
        if (redirect.toUpperCase() === "COLLABS") {
          this.navigateToPaidCollab(true, false);
        } else if (redirect.toUpperCase() === "PLANNEDVISITS") {
          this.navigateToPaidCollab(true, true);
        } else if (redirect.toUpperCase() === "INSPIRE") {
          this.navigateToVisitInspiration(true);
        } else if (redirect.toUpperCase() === "PAGE") {
          this.navigateToCommunityPageAdmin(true);
        } else if (redirect.toUpperCase() === "WIDGET" || redirect.toUpperCase() === "PHOTOWALL") {
          this.navigateToPhotoWalls(true);
        } else if (redirect.toUpperCase() === "ADVWALL") {
          this.navigateToAdventureWalls(true);
        } else if (redirect.toUpperCase() === "QR" || redirect.toUpperCase() === "PRINTMEDIA") {
          this.navigateToPrintMedia(true);
        } else if (redirect.toUpperCase() === "PHOTOS") {
          this.navigateToPhotos(true);
        } else if (redirect.toUpperCase() === "APPROVALS") {
          this.navigateToApprovals(true);
        } else if (redirect.toUpperCase() === "COMMUNITYUPLOAD") {
          this.navigateToCommunityUpload(true);
        } else if (redirect.toUpperCase() === "SHARESTOP") {
          this.navigateToShareStop(true);
        } else if (redirect.toUpperCase() === "SUBSCRIPTION") {
          this.navigateToManageSubscription(true);
        } else if (redirect.toUpperCase() === "USERADMIN") {
          this.navigateToUserAdmin(true);
        } else if (redirect.toUpperCase() === "ADVSUM") {
          this.navigateToAdventureSummary(true);
        } else if (redirect.toUpperCase() === "SAVED") {
          this.navigateToSavedCreators(true);
        } else if (redirect.toUpperCase() === "CALENDAR" || redirect.toUpperCase() === "SOCIALCALENDAR" || redirect.toUpperCase() === "CALENDARTOPICS") {
          this.navigateToContentCalendar(true);
        } else if (redirect.toUpperCase() === "SOCIALSETTINGS" || redirect.toUpperCase() === "SOCIALCONFIG") {
          this.navigateToSocialSettings(true);
        }
      }
    },
    async checkForSpotlightCheckoutId() {
      const spotlightCheckoutId = RouteHelper.getQueryStringParam("stripe_session");
      if (spotlightCheckoutId) {
        // todo: Additional checks (in case they refresh the page with the id on the uri)
        getGlobalRemoteLogger().info(`checkForSpotlightCheckoutId: spotlightCheckoutId=${spotlightCheckoutId}`);
        const customerId = this.communityIdInContext;
        if (!customerId) {
          getGlobalRemoteLogger().error("No community ID in context for Spotlight checkout");
          return;
        }

        const response = await axios.get(`${import.meta.env.VITE_API_URL}/customers/${customerId}/spotlight-payment-complete/${spotlightCheckoutId}`);
        // todo: Show a message?
        getGlobalRemoteLogger().info(`Spotlight PaymentCompletion, result=${response.data}`);
      }
    },
    async loadData(communityIdToSelect: string = null) {
      // Call loadCommunityData first since it defaults the communityIdInContext
      await this.loadCommunityData(communityIdToSelect);
      // Not awaiting these so they run in parallel
      if (this.communityData.length > 0) {
        // Feature flag refresh for Atlas Social
        this.loadFeatureFlags();
        this.loadCustomCollabs();
        this.loadCollabSummaryWithActions();
        this.loadCommunityUploadData();
      }
    },
    async loadCustomCollabs() {
      this.isCustomCollabsLoading = true;
      // Excludes stale collabs after 14 days
      this.customCollabs = await CustomerCustomCollabsRepo.getAllCustomCollabsV2(this.communityIdInContext, this.userProfileStore.getViewingCreatorIdIfImpersonating, 14);
      this.isCustomCollabsLoading = false;
    },
    // Loads the collab summaries.
    // NOTE: communityIdInContext MUST be set before calling this.
    async loadCollabSummaryWithActions() {
      this.isCollabAndCreatorSummaryArrayLoading = true;
      let uri = `${import.meta.env.VITE_API_URL}/collab-summary/customers/${this.communityIdInContext}/summary-with-actions`;
      const { data } = await axios.get<Array<CustomerCollabActionSummary>>(uri);

      this.collabAndCreatorSummaryArray = data;
      this.isCollabAndCreatorSummaryArrayLoading = false;
    },
    async loadFeatureFlags() {
      try {
        this.serverFlagEnableAtlasSocial = await FeatureFlagCache.GetFeature("social-calendar");
      } catch (e) {
        console.error(e);
        this.serverFlagEnableAtlasSocial = false;
      }
    },
    async loadCommunityUploadData(): Promise<void> {
      if (!this.communityIdInContext) return;

      this.isCommunityUploadDataLoading = true;
      const { data } = await axios.get<UserUploadCampaignsV2>(`${import.meta.env.VITE_API_URL}/useruploadcampaigns/admin/${this.communityIdInContext}/v2`);
      this.campaignData = data;
      this.isCommunityUploadDataLoading = false;
    },
    // Loads the passed community data OR defaults if not passed
    async loadCommunityData(communityIdToSelect: string = null) {
      let uri = `${import.meta.env.VITE_API_URL}/pages/dashboard`;

      if (this.superUserManuallyAddedCommunityIds.length > 0) {
        uri += "?";
        this.superUserManuallyAddedCommunityIds.forEach(id => (uri += `adds=${id}&`));
      }

      const response = await axios.get(uri);
      this.communityData = response.data;

      // Auto-select for superuser flow
      if (communityIdToSelect) {
        this.communityIdInContext = communityIdToSelect;
        await this.setOrgInContext(this.getCommunityInContext());
      }

      // The superuser manual add flows means communityData might have nothing in it on the initial load
      if (this.communityData.length > 0) {
        this.defaultSelectedCommunityFromSession();
        await this.setOrgInContext(this.getCommunityInContext());
      }

      this.isLoadingCommunityData = false;
    },
    getCommunityInContext(): CustomerDashboardSummary | null {
      const community = this.communityData.filter(c => c.communityId === this.communityIdInContext);
      if (community && community.length > 0) return community[0];
      else if (this.communityData.length > 0) {
        // Edge case: communityIdInContext is pretty sticky, if a user logs out/in as another in the same session,
        // then they may get a communityInContext that they don't actually have, so just pick the first one they do have;
        // And update the communityIdInContext
        this.communityIdInContext = this.communityData[0].communityId;
        this.setOrgInContext(this.communityData[0]);
        return this.communityData[0];
      }

      return null;
    },
    checkForEmailHint() {
      // Invites pass along the email as a hint so we can direct the signin/up flow to be less confusing (since Auth0s UI isn't great)
      const emailHint = RouteHelper.getQueryStringParam("email");

      if (emailHint && emailHint.length > 0) {
        this.isEmailHint = true;
        this.emailHint = emailHint;

        if (emailHint.toLowerCase().endsWith("gmail.com")) {
          this.isEmailHintGmail = true;
        }
      }
    },
    getCurrentUsersEmail(): string | null {
      return this.getViewingUserProfile?.email;
    },
    async checkForSuperUserFields() {
      if (this.isSuperOrSalesUser) {
        // Re-hydrate the ids that have been added
        const cache = sessionStorage.getItem("SuperUserManualCommunitySelections");
        if (cache) {
          this.superUserManuallyAddedCommunityIds = JSON.parse(cache);
        }
      }
    },
    superUserCustomerSelected(customerId: string) {
      if (customerId && customerId.length > 0) {
        this.superUserManuallyAddedCommunityIds.push(customerId);
        // Save it in sessionStorage since we need this in the list when we use the Back button
        sessionStorage.setItem("SuperUserManualCommunitySelections", JSON.stringify(this.superUserManuallyAddedCommunityIds));
        // Reload the data
        this.loadData(customerId);
      }
    },
    defaultSelectedCommunityFromSession() {
      const selectedCommunityId = AdminContext.getCommunityIdInContext();
      if (selectedCommunityId && selectedCommunityId !== null) {
        this.communityIdInContext = selectedCommunityId;
      }
    },
    async setOrgInContext(org: CustomerDashboardSummary) {
      // setOrgInContext now updates globalRoot.orgInContext internally
      AdminContext.setOrgInContext(
        null,
        org.topNavLogoUri,
        org.communityUniqueName,
        org.communityId,
        org.type,
        org.communityName,
        org.immutableId,
        org.isDisabled,
        org.isSpotlight,
        org.enableCreateAdventures,
        org.trialEndDate,
        org.paymentStatus,
        org.subType
      );

      this.setTitle();
    },
    setTitle() {
      // @ts-ignore
      if (this.globalRoot.orgInContext) {
        // @ts-ignore
        this.title = "Admin Dashboard - " + this.globalRoot.orgInContext.name;
      }
    },
    communityChanged() {
      // Change the icon in the header
      this.setOrgInContext(this.getCommunityInContext());

      this.loadData();
    },
    navigateToCommunityPageAdmin(noHistory = false) {
      const destination = { name: "CommunityPageAdmin", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToPaidCollab(noHistory = false, showPlannedVisits = false) {
      const destination = { name: "PaidCollabCommunityAdmin", params: { communityId: this.communityIdInContext }, hash: "" };
      if (showPlannedVisits) {
        // Add a hash param to show the planned visits modal
        destination.hash = "#showPlannedVisits=true";
      }

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToVisitInspiration(noHistory = false) {
      const destination = { name: "VisitInspiration", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToPhotos(noHistory = false) {
      const destination = { name: "CommunityPhotos", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToApprovals(noHistory = false) {
      // This is no longer used, so just return
      return;
    },
    navigateToCommunityUpload(noHistory = false) {
      const destination = { name: "UserUploadCampaigns", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToShareStop(noHistory = false) {
      // Note: This should probably redirect to Atlas (but we need the smarts of whether to go to the landing page or the content calendar)
      return;
      /* const destination = { name: "DeadPage", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);*/
    },
    navigateToManageSubscription(noHistory = false) {
      const destination = { name: "SubscriptionSettings", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToUserAdmin(noHistory = false) {
      const destination = { name: "UserAdmin", params: { pageId: this.communityIdInContext } };
      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToAdventureSummary(noHistory = false) {
      const destination = { name: "AdventureSummary", params: { pageId: this.communityIdInContext } };
      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToSavedCreators(noHistory = false) {
      const destination = { name: "CustomerSavedCreators", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToContentCalendar(noHistory = false) {
      const destination = { name: "CalendarPosts", params: { customerId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToSocialSettings(noHistory = false) {
      const destination = { name: "CalendarConfig", params: { customerId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToPhotoWalls(noHistory = false) {
      const destination = { name: "WidgetDirections", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToAdventureWalls(noHistory = false) {
      const destination = { name: "AdventureWidgets", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    navigateToPrintMedia(noHistory = false) {
      const destination = { name: "PrintMedia", params: { pageId: this.communityIdInContext } };

      if (noHistory === true) this.$router.replace(destination);
      else this.$router.push(destination);
    },
    logout() {
      getInstance().logout();
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/modal-close-button.scss";

// Spotlight dashboard wrapper ================================================
.spotlight-dashboard-wrapper {
  gap: 0 $desktop-wide-grid-gap-width;
  display: flex;
  flex-wrap: wrap;

  &__spotlight-dashboard {
    display: contents;
  }

  :deep(.spotlight-dashboard__divider) {
    width: 100%;
  }

  :deep(.photo-walls-section) {
    // width: calc(50% - $desktop-wide-grid-gap-width / 2);
  }

  &__highlights {
    width: calc(50% - $desktop-wide-grid-gap-width / 2);
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .spotlight-dashboard-wrapper {
    :deep(.photo-walls-section) {
      width: 100%;
      margin-bottom: 40px;
    }

    &__highlights {
      width: 100%;
    }
  }
}

// Greeting section ===========================================================
.greeting-section {
  gap: 15px;
  display: flex;
  align-items: flex-end;

  &__title-n-text {
    flex-grow: 1;
    margin-right: 20px;

    &:last-child {
      margin-right: 0;
    }
  }

  &__title {
    margin-bottom: 10px;

    &--with-select {
      display: flex;
      align-items: center;
    }
  }

  &__community-select {
    margin-left: 15px;
    padding-bottom: 0 !important;
  }

  &__text,
  &__text p {
    margin-bottom: 0;
    font: 400 14px/22px sans-serif;
  }

  &__superuser-field {
    width: 200px;
    min-width: 200px;
    margin-top: 40px;
    opacity: 0.6;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  // Greeting section ===========================================================
  .greeting-section {
    flex-direction: column;
    align-items: flex-start;

    &__title-n-text {
      margin-right: 0;
    }

    &__superuser-field {
      margin-top: 15px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .greeting-section {
    flex-direction: column;
    align-items: flex-start;

    &__title-n-text {
      margin-right: 0;
    }

    &__title {
      margin-bottom: 7px;

      &--with-select {
        flex-wrap: wrap;
        gap: 10px;
      }
    }

    &__community-select {
      margin-left: 0;
    }

    &__superuser-field {
      margin-top: 15px;
    }
  }
}

// Admin controls =============================================================
.admin-controls {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  z-index: 0;

  &__close-btn {
    position: absolute;
    inset: -10px -10px auto auto;
    z-index: 2;
  }

  &__multiple-communities-admin {
    width: 100%;
    padding: 19px 25px 19px;
    box-sizing: border-box;
    display: inline-flex;
    flex-direction: column;
    position: relative;
    z-index: 0;

    &::before {
      content: "";
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      border-radius: 6px;
      position: absolute;
      inset: 0 auto auto 0;
      z-index: -1;
      background: #f3f3f3;
    }
  }
}

// Dashboard content ==========================================================
.dashboard-content {
  max-width: 1050px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  color: #5b5b5b;
  font: normal 14px/19px sans-serif;

  &__superuser-panel {
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0.8;
    z-index: 10;
  }

  &__greeting-section {
    margin-bottom: 25px;
  }

  &__horizontal-divider {
    margin-bottom: 35px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__highlights-list {
    width: 100%;
    margin-bottom: 70px;
  }

  &__admin-controls {
    margin-bottom: 45px;
  }

  &__we-love-snippet {
    margin-top: 30px;
    margin-bottom: 50px;
    width: 100%;
  }
}

// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .dashboard-content {
    &__greeting-section {
      margin-bottom: 25px;
    }

    &__horizontal-divider {
      margin-bottom: 30px;
    }

    &__highlights-list {
      margin-bottom: 55px;
    }
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .dashboard-content {
    &__greeting-section {
      margin-bottom: 20px;
    }

    &__horizontal-divider {
      margin-bottom: 29px;
    }

    &__highlights-list {
      margin-bottom: 54px;
    }
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .dashboard-content {
    &__greeting-section {
      margin-bottom: 20px;
    }

    &__horizontal-divider {
      margin-bottom: 29px;
    }

    &__highlights-list {
      margin-bottom: 53px;
    }
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .dashboard-content {
    &__superuser-panel {
      position: static;
      margin-bottom: 20px;
      opacity: 1;
    }

    &__greeting-section {
      margin-bottom: 20px;
    }

    &__horizontal-divider {
      margin-bottom: 25px;
    }

    &__highlights-list {
      margin-bottom: 40px;
    }
  }
}

// Spotlight dashboard ========================================================
.spotlight-dashboard {
  &__main-header {
    width: 100%;
    margin-bottom: 18px;
  }

  &__upload-campaigns {
    width: 100%;
    margin-bottom: 48px;
  }

  &__divider {
    margin-bottom: 35px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__photo-walls {
    margin-bottom: 45px;
  }
}

// Main header ================================================================
.main-header {
  &__title {
    margin-bottom: 6px;
    text-decoration: underline;
    text-decoration-color: rgba(0, 0, 0, 0.2);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    text-decoration-style: solid;

    &:hover {
      text-decoration: none;
    }
  }

  &__subtitle {
    margin-top: 5px;
  }
}

// Upload campaigns ===========================================================
.upload-campaigns {
  &__template-snippets-list {
    padding: 0;
    margin: 0;
    gap: $desktop-wide-grid-gap-width;
    display: flex;
    list-style: none;
  }

  &__template-snippet {
  }

  &__create-link {
  }

  &__upload-campaign-snippets-list {
    width: 100%;
    max-width: 100%;
    gap: 27px;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    list-style: none;
  }

  &__upload-campaign-snippet {
  }
}

// Photo walls section ========================================================
.photo-walls-section {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &__header {
    margin-bottom: 20px;
  }

  &__title {
    margin-bottom: 6px;
    text-decoration: underline;
    text-decoration-color: rgba(0, 0, 0, 0.2);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    text-decoration-style: solid;

    &:hover {
      text-decoration: none;
    }
  }

  &__subtitle {
    margin-top: 5px;
  }

  &__example-img {
    width: 400px;
    max-width: 100%;
    margin-bottom: 23px;
  }

  &__create-photowall-snippet {
  }
}

// Responsive styles
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .upload-campaigns {
    &__template-snippets-list {
      gap: $tablet-large-grid-gap-width;
    }

    &__template-snippet {
      width: calc(50% - #{$tablet-large-grid-gap-width} / 2);
    }
  }
}

@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .upload-campaigns {
    &__template-snippets-list {
      gap: $tablet-grid-gap-width;
      flex-wrap: wrap;
    }

    &__template-snippet {
      width: calc(50% - #{$tablet-grid-gap-width} / 2);
    }
  }
}

@media (max-width: $mobile-max-width) {
  .upload-campaigns {
    &__template-snippets-list {
      gap: $mobile-grid-gap-width;
      flex-wrap: wrap;
    }

    &__template-snippet {
      width: calc(50% - #{$mobile-grid-gap-width} / 2);
    }
  }
}
</style>
